import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { compose } from 'redux';
import { withTranslation } from 'react-i18next';
import { cloneDeep } from 'lodash';
import moment from 'moment';
import { MainHeader } from '../../components/Header';
import injectSaga from '../../utils/injectSaga';
import injectReducer from '../../utils/injectReducer';
import { TopUp } from '../../components/TopUp';
import { getAccountId, getUserName } from '../../localStorage';
import { checkPermissionApplyPayment } from '../../utils/CheckPermissions';
import { makeGetPermissionSelfCare, makeGetAccountInfo, makeGetCcpProperty } from '../App/selectors';
import { FormHeader } from '../../components/form';
import makeSelectTopUpPage from './selectors';
import { GenericInput } from '../../components/common';
import reducer from './reducer';
import saga from './saga';
import {
  getAccountById, manageAccount, applyPayment, applyExternalPayment,
  searchSubscriptionByAccountId, searchSubscriptionTopUp, getCcpTime, getBalanceUnitByAccountId,
  getTopUpReceipt, readFileFromS3,
} from '../App/actions';
import createPlaceToPaySession from '../../api/payments/createPlaceToPaySession';
import verifyPlaceToPaySession from '../../api/payments/verifyPlaceToPaySession';
import capturePaymentToken from '../../api/payments/capturePaymentToken';
import prepaidTopUp from '../../api/payments/prepaidTopUp';
import './styles.scss';

const PLACETOPAY_SESSION_KEY = 'p2p_pending';

// P5 (engine) local error-code literal for a rejected duplicate top-up
// (PGSubscriptionTopUpService.DUPLICATE_TOPUP). Surfaced non-fatally here, not as a hard error.
const DUPLICATE_TOPUP_ERROR_CODE = 'DUPLICATE_TOPUP';
const isDuplicateTopUp = res => !!(res && res.errorCode === DUPLICATE_TOPUP_ERROR_CODE);

// Top-up eligibility. prepaidTopUp credits a subscription's balance unit; with no payable
// subscription the engine call fails AFTER the card/PlaceToPay charge has already been
// captured (money taken, nothing credited). Both pay paths are gated on this.
// CLOSED subscriptions are not payable; SUSPENDED are (topping up is how a user reconnects).
const ELIGIBILITY = {
  LOADING: 'LOADING',
  ELIGIBLE: 'ELIGIBLE',
  NO_SUBSCRIPTION: 'NO_SUBSCRIPTION',
  UNKNOWN: 'UNKNOWN', // subscription lookup failed - cannot confirm, so do not take money
};
const isPayableSubscription = sub => !!(sub && sub.id && sub.status !== 'CLOSED');

// PlaceToPay verification UI state (banner displayed at top of the make-payment page
// when the user returns from PlaceToPay's hosted checkout).
const P2P_UI = {
  IDLE: 'IDLE',
  CHECKING: 'CHECKING',
  APPROVED: 'APPROVED',
  REJECTED: 'REJECTED',
  EXPIRED: 'EXPIRED',
  AMOUNT_MISMATCH: 'AMOUNT_MISMATCH',
  ERROR: 'ERROR',
  PROCESSING_DUPLICATE: 'PROCESSING_DUPLICATE', // duplicate / lock-unavailable: non-terminal, keep reference
};

// Generates a per-intent reference. Prefers crypto.randomUUID (no `uuid` package
// dependency in this project); falls back for older browsers/test environments.
const generateUuid = () => (
  (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function')
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`
);

const topUpTableColumns = [
  { name: 'createdDate', label: 'label.date' },
  { name: 'amount', label: 'label.quantity' },
  { name: 'paymentSource', label: 'label.paymentMethod' },
];

export class TopUpPage extends React.PureComponent {

  constructor() {
    super();

    this.state = {
      creditCards: [{}],
      topUpData: [],
      topUpAmount: '',
      p2pUiState: P2P_UI.IDLE,
      topUpReference: null,
      p2pDetails: {},
      p2pErrorMessage: '',
      isFirstDayOfMonth: false,
      minimumAmount: 0,
      totalBalance: null,
      isFirstCycle: null,
      receiptLoadingId: null,
      topUpEligibility: ELIGIBILITY.LOADING,
    };
  }

  componentDidMount() {
    this.doGetAccountDetails();
    this.doGetBalance();
    this.doGetTopUpHistory();

    // If we're returning from a PlaceToPay redirect, resume the verify+apply flow.
    // The trigger is the sessionStorage key written before redirecting out.
    this.resumePlaceToPayFlowIfPending();
  }

  /**
   * Evaluates the Minimum Top Up rules ONLY after both
   * the Balance and the Historical Top Up queries have finished.
   */
  calculateMinimumTopUp = () => {
    const { totalBalance, isFirstCycle } = this.state;

    if (totalBalance === null || isFirstCycle === null) return;

    // Do nothing if we are still waiting for either API to return
    const baseline = isFirstCycle ? 2920 : 3300;
    let minTopUp = 0;

    if (totalBalance > 0) {
      // User is in debt
      minTopUp = baseline + totalBalance;
    } else if (totalBalance <= 0 && Math.abs(totalBalance) < baseline) {
      // User has a credit (or exactly 0), but it's not enough to cover the baseline
      minTopUp = Math.round(baseline - Math.abs(totalBalance));
    } else {
      // User has a massive credit that completely covers the baseline
      minTopUp = 0;
    }

    this.setState({ minimumAmount: minTopUp });
  };

  doGetBalance = () => {
    const { getBalanceUnitByAccountId } = this.props;
    const accountId = getAccountId();

    getBalanceUnitByAccountId({ id: accountId }, (res) => {
      let balanceUnits = [];
      if (res && res.data) {
        const rawData = res.data.getBalanceUnitByAccountId || res.data;
        balanceUnits = Array.isArray(rawData) ? rawData : [rawData];
      }

      let balanceAcc = 0;

      balanceUnits.forEach((unit) => {
        if (unit && unit.balances) {
          unit.balances.forEach((b) => {
            if (b.amount !== undefined && b.amount !== null) {
              // Check if the backend returned a flat number or string (e.g., -1.0)
              if (typeof b.amount === 'number' || typeof b.amount === 'string') {
                balanceAcc += Number(b.amount);
              }
              // Fallback for the nested object structure (e.g., { parsedValue: -308500 })
              else if (b.amount.parsedValue !== undefined) {
                balanceAcc += Number(b.amount.parsedValue);
              }
              else if (b.amount.source !== undefined) {
                balanceAcc += Number(b.amount.source);
              }
            }
          });
        }
      });

      // Save to state and attempt calculation
      this.setState({ totalBalance: balanceAcc }, this.calculateMinimumTopUp);
    });
  };

  doGetTopUpHistory = () => {
    const { searchSubscriptionByAccountId, searchSubscriptionTopUp, getCcpTime } = this.props;
    const accountId = getAccountId();

    getCcpTime({ dummy: 'Hello' }, (ccpResult) => {
      let currentCcpDate = moment(); // Fallback to today's date

      if (ccpResult && ccpResult.success && ccpResult.data) {
        const data = ccpResult.data;
        const timeStr = data.ccpTime || (data.getCcpDateTime && data.getCcpDateTime.ccpTime);

        if (timeStr) {
          currentCcpDate = moment(timeStr, 'YYYY-MM-DD');
        }
      }

      // Set 1st DOM state
      this.setState({ isFirstDayOfMonth: currentCcpDate.date() === 1 });

      // Calculate the first and last day of that month
      const startDate = currentCcpDate.clone().startOf('month').format('YYYY-MM-DD');
      const endDate = currentCcpDate.clone().endOf('month').format('YYYY-MM-DD');

      // Fetch Subscriptions
      searchSubscriptionByAccountId(
        { page: 1, size: 20, accountId },
        (subResult) => {
          const lookupSucceeded = !!(subResult && subResult.success);
          const subscriptions = (lookupSucceeded && subResult.data) ? subResult.data : [];
          const payableSubscriptions = subscriptions.filter(isPayableSubscription);

          // Payment gate: only an account with at least one payable subscription can top up.
          let topUpEligibility = ELIGIBILITY.UNKNOWN;
          if (lookupSucceeded) {
            topUpEligibility = payableSubscriptions.length
              ? ELIGIBILITY.ELIGIBLE
              : ELIGIBILITY.NO_SUBSCRIPTION;
          }
          this.setState({ topUpEligibility });

          // No subscriptions
          if (!subscriptions.length) {
            this.setState({ topUpData: [], isFirstCycle: true }, this.calculateMinimumTopUp);
            return;
          }

          const primarySub = subscriptions[0];
          let isFirst = true;

          if (primarySub.effectiveDate) {
            const effectiveMoment = moment(primarySub.effectiveDate, 'YYYY-MM-DD');
            // Snaps exactly to the last day of the effective month (e.g., July 31st)
            const cycleEndMoment = effectiveMoment.clone().endOf('month');
            isFirst = currentCcpDate.isSameOrBefore(cycleEndMoment, 'day');
          }

          this.setState({ isFirstCycle: isFirst }, this.calculateMinimumTopUp);

          const topUpPromises = subscriptions.map((sub) => {
            return new Promise((resolve) => {
              searchSubscriptionTopUp(
                {
                  page: 1,
                  size: 20,
                  accountId,
                  subscriptionId: sub.id,
                  startDate,
                  endDate,
                },
                (tuResult) => {
                  const topUps = (tuResult && tuResult.success && tuResult.data) ? tuResult.data : [];
                  resolve(topUps);
                }
              );
            });
          });

          // Wait for all queries to finish, then flatten into one array
          Promise.all(topUpPromises).then((resultsArray) => {
            const mergedData = resultsArray.reduce((acc, val) => acc.concat(val), []);
            this.setState({ topUpData: mergedData });
          });
        }
      );
    });
  };

  resumePlaceToPayFlowIfPending = () => {
    if (this.p2pResumeFired) return;

    let pending = null;
    try {
      const raw = sessionStorage.getItem(PLACETOPAY_SESSION_KEY);
      if (raw) pending = JSON.parse(raw);
      if (raw) sessionStorage.removeItem(PLACETOPAY_SESSION_KEY);
    } catch (e) {}

    if (!pending || !pending.requestId) return;

    this.p2pResumeFired = true;
    this.setState({ p2pUiState: P2P_UI.CHECKING });

    verifyPlaceToPaySession(pending.requestId, {
      amount: pending.amount,
      currency: pending.currency,
    })
      .then((verifyResult) => {
        if (!verifyResult) {
          this.setState({ p2pUiState: P2P_UI.ERROR, p2pErrorMessage: 'No response from verify' });
          return;
        }
        if (verifyResult.successful === true) {
          // P2P redirect-resume: `pending.reference` is the same stable per-intent reference
          // minted in onPayWithPlaceToPay and round-tripped via sessionStorage - already in
          // scope here, so it's forwarded to prepaidTopUp (P6 step 3).
          const topUpPayload = {
            accountId: pending.accountId || getAccountId(),
            serviceType: "ELECTRICITY",
            paymentSource: "CREDIT_CARD",
            paymentDescription: `Embrix Top Up ${pending.amount}`,
            amount: Number(pending.amount),
            reference: pending.reference,
          };

          prepaidTopUp(topUpPayload)
            .then((res) => {
              if (res && res.status === 'SUCCESS') {
                this.setState({
                  p2pUiState: P2P_UI.APPROVED,
                  p2pDetails: { ...verifyResult, topUpResult: res },
                });
                this.doGetTopUpHistory(); // Refresh the table
              } else if (isDuplicateTopUp(res)) {
                // Non-fatal: already processed, no double-charge, no error-loop.
                this.setState({
                  p2pUiState: P2P_UI.PROCESSING_DUPLICATE,
                  p2pDetails: { ...verifyResult, topUpResult: res },
                });
                this.doGetTopUpHistory();
              } else {
                this.setState({
                  p2pUiState: P2P_UI.ERROR,
                  p2pErrorMessage: `Top Up Failed: ${res.status || 'Unknown error'}`
                });
              }
            })
            .catch((err) => {
              this.setState({ p2pUiState: P2P_UI.ERROR, p2pErrorMessage: err.message });
            });
        } else if (verifyResult.status === 'AMOUNT_MISMATCH') {
          this.setState({ p2pUiState: P2P_UI.AMOUNT_MISMATCH, p2pDetails: verifyResult });
        } else if (verifyResult.status === 'EXPIRED') {
          this.setState({ p2pUiState: P2P_UI.EXPIRED, p2pDetails: verifyResult });
        } else {
          this.setState({ p2pUiState: P2P_UI.REJECTED, p2pDetails: verifyResult });
        }
      })
      .catch((err) => {
        this.setState({
          p2pUiState: P2P_UI.ERROR,
          p2pErrorMessage: (err && err.message) || String(err),
        });
      });
  };

  renderPlaceToPayBanner = () => {
    const { t } = this.props;
    const { p2pUiState, p2pDetails, p2pErrorMessage } = this.state;
    if (p2pUiState === P2P_UI.IDLE) return null;

    const baseStyle = {
      padding: '14px 18px',
      margin: '12px 0',
      borderRadius: 4,
      borderLeft: '4px solid',
      background: '#f7f8fa',
    };
    const map = {
      CHECKING:        { color: '#2547a5', title: t('label.p2pBanner.checkingTitle'),
        text: t('message.p2pBanner.checkingText') },
      APPROVED:        { color: '#2e7d32', title: t('label.p2pBanner.approvedTitle'),
        text: t('message.p2pBanner.approvedText')(p2pDetails.receipt, p2pDetails.authorizationCode, p2pDetails.amount, p2pDetails.currency) },
      REJECTED:        { color: '#c62828', title: t('label.p2pBanner.rejectedTitle'),
        text: t('message.p2pBanner.rejectedText')(p2pDetails.message || p2pDetails.status) },
      EXPIRED:         { color: '#ef6c00', title: t('label.p2pBanner.expiredTitle'),
        text: t('message.p2pBanner.expiredText') },
      AMOUNT_MISMATCH: { color: '#c62828', title: t('label.p2pBanner.amountMismatchTitle'),
        text: t('message.p2pBanner.amountMismatchText')(p2pDetails.reference) },
      ERROR:           { color: '#c62828', title: t('label.p2pBanner.errorTitle'),
        text: t('message.p2pBanner.errorText')(p2pErrorMessage) },
      PROCESSING_DUPLICATE: { color: '#ef6c00', title: t('label.p2pBanner.processingDuplicateTitle'),
        text: t('message.p2pBanner.processingDuplicateText') },
    };
    const e = map[p2pUiState];
    if (!e) return null;
    return (
      <div style={{ ...baseStyle, borderLeftColor: e.color }}>
        <h4 style={{ margin: '0 0 6px 0', color: e.color }}>{e.title}</h4>
        <p style={{ margin: 0 }}>{e.text}</p>
      </div>
    );
  };

  doGetAccountDetails = () => {
    const { accountDetails, getAccountById } = this.props;
    if (!accountDetails || !accountDetails.id || accountDetails.id !== getAccountId()) {
      getAccountById({ id: getAccountId() }, ({ success, data }) => {
        if (success && data && data.paymentProfiles && data.paymentProfiles[0] && data.paymentProfiles[0].creditCards) {
          this.setState({ creditCards: data.paymentProfiles[0].creditCards });
        }
      });
    } else {
      this.setState({
        creditCards: accountDetails?.paymentProfiles[0]?.creditCards ? accountDetails.paymentProfiles[0].creditCards : [{}],
      });
    }
  };

  handleAddCreditCardFromURL = payload => {
    this.props.manageAccount(payload, success => {
      if (success) {
        this.props.history.push('/top-up');
        this.doGetAccountDetails();
      }
    });
  };

  onChangeTopUpAmount = (e) => {
    const numericValue = e.target.value.replace(/[^0-9]/g, '');
    this.setState({ topUpAmount: numericValue });
  };

  // Blocks both pay paths unless the account has a subscription prepaidTopUp can credit.
  // Returning false here is what prevents the charge-succeeds/top-up-fails money loss.
  canStartTopUp = () => {
    const { t } = this.props;
    const { topUpEligibility } = this.state;

    if (topUpEligibility === ELIGIBILITY.ELIGIBLE) return true;

    if (topUpEligibility === ELIGIBILITY.NO_SUBSCRIPTION) {
      alert(t('message.topUpNoSubscription'));
    } else {
      // LOADING or UNKNOWN: eligibility not confirmed, do not let a charge start.
      alert(t('message.topUpEligibilityUnknown'));
    }
    return false;
  };

  onPayWithPlaceToPay = () => {
    if (!this.canStartTopUp()) return;

    const { topUpAmount } = this.state;
    const amount = Number(topUpAmount) || 0;

    if (!amount || amount <= 0) {
      alert('Please enter a valid amount to Top Up.');
      return;
    }

    const accountId = getAccountId();
    // Generate a unique reference since there is no specific invoice unit ID
    const reference = `TOPUP-${accountId}-${Date.now()}`;

    const { accountDetails } = this.props;
    const currency = accountDetails && accountDetails.currency;

    if (!currency) {
      alert('This account has no currency configured. PlaceToPay cannot create a session. Please contact support.');
      return;
    }

    const returnUrl = `${window.location.origin}/top-up`;

    const body = {
      amount,
      currency,
      reference,
      description: `Embrix Top Up for ${accountId}`,
      returnUrl,
    };

    createPlaceToPaySession(body)
      .then((session) => {
        if (!session || !session.processUrl) {
          throw new Error('payment-gateway returned no processUrl');
        }
        try {
          sessionStorage.setItem(
            PLACETOPAY_SESSION_KEY,
            JSON.stringify({
              requestId: session.requestId,
              accountId,
              amount,
              currency,
              reference,
            }),
          );
        } catch (e) { }
        window.location.assign(session.processUrl);
      })
      .catch((err) => {
        alert(`Could not start PlaceToPay session: ${err && err.message ? err.message : err}`);
      });
  };

  onPay = () => {
    if (this.state.p2pUiState === P2P_UI.CHECKING) return; // in-flight: no-op
    if (!this.canStartTopUp()) return;

    const { topUpAmount, creditCards } = this.state;
    const { accountDetails } = this.props;

    const amount = Number(topUpAmount) || 0;

    if (!amount || amount <= 0) {
      alert('Please enter a valid amount to Top Up.');
      return;
    }

    if (!creditCards || !creditCards.length || !creditCards[0].cardToken) {
      alert('Missing saved card token. Please add a card first.');
      return;
    }

    const savedCard = creditCards[0];
    const isPlaceToPay = savedCard.merchant === 'PLACETOPAY';
    const accountId = getAccountId();

    // PlaceToPay Token flow
    if (isPlaceToPay) {
      const currency = accountDetails && accountDetails.currency;
      if (!currency) {
        alert('Account currency is missing. Cannot process token charge.');
        return;
      }

      // Mint once per intent; reuse the same reference across retries so the
      // server-side idempotency lock (Phase 2) recognizes a retried request.
      // PlaceToPay caps payment.reference at 32 chars. Mint a short, unique, stable-per-intent
      // reference (held in state, reused on retry) that fits: prefix + accountId + 12 hex, hard-
      // capped at 32. A full UUID (53 chars total) overflows and the vendor 400s the charge.
      const reference =
        this.state.topUpReference ||
        `TU-${accountId}-${generateUuid().replace(/-/g, '').slice(0, 12)}`.slice(0, 32);
      this.setState({ p2pUiState: P2P_UI.CHECKING, topUpReference: reference });

      const capturePayload = {
        vendor: "PLACETOPAY",
        operation: "CAPTURE",
        reference,
        description: `Top Up Payment via Saved Token`,
        amount,
        currency,
        buyerEmail: accountDetails?.email || 'noreply@embrix.com',
        creditCard: {
          vendor: "PLACETOPAY",
          paymentMethodToken: savedCard.cardToken
        }
      };

      capturePaymentToken(capturePayload)
        .then((captureResult) => {
          if (captureResult && captureResult.successful) {

            // Build the Top Up payload after capture succeeds. `reference` is the same
            // stable per-intent reference used for the card charge above (P6 step 3) - already
            // in scope via the `reference` const/closure.
            const topUpPayload = {
              accountId,
              serviceType: "ELECTRICITY",
              paymentSource: "CREDIT_CARD",
              paymentDescription: `Embrix Top Up ${amount}`,
              amount,
              reference,
            };

            // Prepaid Top Up
            prepaidTopUp(topUpPayload)
              .then((res) => {
                if (res && res.status === 'SUCCESS') {
                  this.setState({
                    p2pUiState: P2P_UI.APPROVED,
                    p2pDetails: { ...captureResult, topUpResult: res },
                    topUpAmount: '', // Clear the input box
                    topUpReference: null, // true terminal: next top-up mints a new reference
                  });
                  this.doGetTopUpHistory();
                } else if (isDuplicateTopUp(res)) {
                  // Non-fatal: already processed, no double-charge, no error-loop.
                  this.setState({
                    p2pUiState: P2P_UI.PROCESSING_DUPLICATE,
                    p2pDetails: { ...captureResult, topUpResult: res },
                    topUpAmount: '',
                    topUpReference: null, // already processed = terminal, next top-up mints fresh
                  });
                  this.doGetTopUpHistory();
                } else {
                  // transient failure: keep topUpReference so a retry reuses the same lock key
                  this.setState({
                    p2pUiState: P2P_UI.ERROR,
                    p2pErrorMessage: `Top Up Failed: ${res.status || 'Unknown error'}`
                  });
                }
              })
              .catch((err) => {
                // transient failure: keep topUpReference so a retry reuses the same lock key
                this.setState({ p2pUiState: P2P_UI.ERROR, p2pErrorMessage: err.message });
              });

          } else if (captureResult && (captureResult.status === 'DUPLICATE_TRANSACTION'
              || captureResult.status === 'LOCK_UNAVAILABLE')) {
            // non-terminal: keep topUpReference, do NOT call prepaidTopUp
            this.setState({ p2pUiState: P2P_UI.PROCESSING_DUPLICATE, p2pDetails: captureResult });
            this.doGetTopUpHistory(); // escape path: let the user see the completed charge
          } else {
            // true vendor decline: server RELEASED the lock => reset reference
            this.setState({ p2pUiState: P2P_UI.REJECTED, p2pDetails: captureResult, topUpReference: null });
          }
        })
        .catch((err) => {
          // transient failure: keep topUpReference so a retry reuses the same lock key
          this.setState({
            p2pUiState: P2P_UI.ERROR,
            p2pErrorMessage: err.message || String(err),
          });
        });

      return;
    }

    // Standard Braintree/Fiserv flow. No stable per-intent `reference` is in scope on this path
    // (EBRX-3, deferred gap) - intentionally NOT minted/added here; behaves as today.
    const topUpPayload = {
      accountId,
      serviceType: "ELECTRICITY",
      paymentSource: "CREDIT_CARD",
      paymentDescription: `Embrix Top Up ${amount}`,
      amount
    };

    prepaidTopUp(topUpPayload)
      .then((res) => {
        if (res && res.status === 'SUCCESS') {
          this.doGetTopUpHistory();
          this.setState({ topUpAmount: '' });
          alert('Top Up payment processed successfully.');
        } else if (isDuplicateTopUp(res)) {
          // Non-fatal: already processed, no double-charge, no error-loop.
          this.doGetTopUpHistory();
          this.setState({ topUpAmount: '' });
          alert('This top-up was already processed. No additional charge was made.');
        } else {
          alert(`Top Up failed: ${res.status || 'Unknown error'}`);
        }
      })
      .catch((err) => {
        alert(`Error processing Top Up: ${err.message}`);
      });
  };

  // Fetches the S3 object key for a top-up's receipt, then a fresh 300s presigned URL for it,
  // then opens it (two-step fetch reusing shipped plumbing: getTopUpReceipt + getS3PresignedUrl
  // via the existing readFileFromS3 action). Button is only rendered when the
  // `topupReceiptsEnabled` CCP flag is on (see renderContent) - flag-off denies server-side too.
  onViewReceipt = (topupId) => {
    const { getTopUpReceipt, readFileFromS3 } = this.props;
    this.setState({ receiptLoadingId: topupId });

    getTopUpReceipt({ topupId }, ({ success, data: objectKey }) => {
      if (!success || !objectKey) {
        this.setState({ receiptLoadingId: null });
        alert('No se pudo obtener el recibo.');
        return;
      }

      readFileFromS3({ url: objectKey }, ({ success: s3Success, data: blob }) => {
        this.setState({ receiptLoadingId: null });
        if (s3Success && blob) {
          window.open(URL.createObjectURL(blob));
        } else {
          alert('No se pudo abrir el recibo.');
        }
      });
    });
  };

  renderContent() {
    const {
      topUpData, creditCards, topUpAmount, minimumAmount, isFirstDayOfMonth, receiptLoadingId,
      topUpEligibility,
    } = this.state;
    const { ccpProperty, t } = this.props;

    // Minimum Top Up Visibility
    // Only show if the mathematically required amount > 0
    const showMinAmountBox = minimumAmount > 0;

    // topupReceiptsEnabled CCP flag (P6) - same read pattern as historicalFolioLink elsewhere
    // in this app (ViewInvoicesPage/ViewPayment): find the property in ccpPropertyList, compare
    // its string value. Off => receipt column/button is not rendered (defense-in-depth only;
    // the resolver itself denies when the flag is off - see GATE A note in QueryResolver).
    let topupReceiptsEnabled = false;
    if (ccpProperty && ccpProperty.ccpPropertyList && ccpProperty.ccpPropertyList.length) {
      const topupReceiptsProperty = ccpProperty.ccpPropertyList.find(
        val => val.property === 'topupReceiptsEnabled'
      );
      if (topupReceiptsProperty) {
        topupReceiptsEnabled = topupReceiptsProperty.value === 'true' || topupReceiptsProperty.value === true;
      }
    }

    const tableColumns = topupReceiptsEnabled
      ? [
          ...topUpTableColumns,
          {
            name: 'receipt',
            label: 'label.receipt',
            render: (colName, item) => (
              <button
                type="button"
                className="btn btn-outline-success btn-sm"
                disabled={receiptLoadingId === item.id}
                onClick={() => this.onViewReceipt(item.id)}
              >
                {receiptLoadingId === item.id ? '...' : t('label.viewDownloadReceipt')}
              </button>
            ),
          },
        ]
      : topUpTableColumns;

    return (
      <div className="payment">
        {this.renderPlaceToPayBanner()}
        <FormHeader
          title="Top Up"
          detail={{ name: 'Account Number:', value: getAccountId() }}
          className="large-font-size"
        />
        <TopUp
          history={this.props.history}
          data={creditCards}
          topUpData={topUpData}
          topUpTableColumns={tableColumns}
          topUpAmount={topUpAmount}
          onChangeTopUpAmount={this.onChangeTopUpAmount}
          onPay={this.onPay}
          onPayWithPlaceToPay={this.onPayWithPlaceToPay}
          minimumAmount={minimumAmount}
          showMinAmountBox={showMinAmountBox}
          processing={this.state.p2pUiState === P2P_UI.CHECKING}
          canTopUp={topUpEligibility === ELIGIBILITY.ELIGIBLE}
          topUpBlockedMessage={
            topUpEligibility === ELIGIBILITY.NO_SUBSCRIPTION
              ? t('message.topUpNoSubscription')
              : (topUpEligibility === ELIGIBILITY.UNKNOWN ? t('message.topUpEligibilityUnknown') : '')
          }
        />
      </div>
    );
  }

  render() {
    return (
      <div className="top-up-page">
        <MainHeader activeTab="TopUp" />
        <div className="top-up-page__form">
          <div className="form-top-up">{this.renderContent()}</div>
        </div>
      </div>
    );
  }
}

TopUpPage.propTypes = {
  history: PropTypes.object,
  manageAccount: PropTypes.func,
  permissionsSelfCare: PropTypes.object,
  accountDetails: PropTypes.object,
  getAccountById: PropTypes.func,
  searchSubscriptionByAccountId: PropTypes.func,
  searchSubscriptionTopUp: PropTypes.func,
  getCcpTime: PropTypes.func,
  getBalanceUnit: PropTypes.func,
  getBalanceUnitByAccountId: PropTypes.func,
  applyExternalPayment: PropTypes.func,
  ccpProperty: PropTypes.object,
  getTopUpReceipt: PropTypes.func,
  readFileFromS3: PropTypes.func,
};

const mapStateToProps = createStructuredSelector({
  topUp: makeSelectTopUpPage(),
  permissionsSelfCare: makeGetPermissionSelfCare() || {},
  accountDetails: makeGetAccountInfo() || {},
  ccpProperty: makeGetCcpProperty() || {},
});

const withConnect = connect(mapStateToProps, {
  getAccountById,
  manageAccount,
  searchSubscriptionByAccountId,
  searchSubscriptionTopUp,
  getCcpTime,
  getBalanceUnitByAccountId,
  applyPayment,
  applyExternalPayment,
  getTopUpReceipt,
  readFileFromS3,
});

const withReducer = injectReducer({ key: 'topUpReducer', reducer });
const withSaga = injectSaga({ key: 'topUpSaga', saga });

export default withTranslation('common')(compose(withReducer, withSaga, withConnect)(TopUpPage));